// «Мои выгрузки» (002, T045; contracts/ui.md, «/audit/exports»; FR-053 – FR-056): свои заказы
// выгрузки журнала в фоне — когда заказано, условия словами, сколько записей, состояние (бейдж
// со словом) и до какого времени хранится файл; новые сверху, «Показать ещё». У неудавшейся —
// причина словами и «Код … · операция …» заказа с «Скопировать номер» (FR-028). Действия
// строки — в меню «…» в конце строки (видна при наведении и фокусе, правый клик — то же меню;
// README «Плотные таблицы», T146): «Скачать файл» у готовой, «Заказать снова» у неудавшейся,
// за разделителем красное «Удалить выгрузку» у обеих — действие и объект (README «Тексты»,
// components/Dropdown/README.md). Панель над таблицей — только
// «Колонки»: поиска, фильтра и сортировки у нескольких временных заказов нет (plan.md,
// «Обоснованные отступления»).
// Удаление — строка сразу скрывается, тост «Файл удалён · Отменить» 10 секунд; запрос уходит,
// когда тост закрылся без «Отменить» (contracts/ui.md, «Действия и подтверждения»). Тост живёт
// дольше страницы и сеанса: «Выйти» выполняет ждущее удаление до выхода, вход другого
// пользователя его отбрасывает, закрытие вкладки — запрос с keepalive (app/deferred.ts, T121).
// Удаление не прошло (и без связи) — строка возвращается, тост-ошибка с «Повторить»: оно снова
// удаляет выгрузку (ux.md, раздел 2).
// ?focus=<номер заказа> — переход из уведомления «Выгрузка журнала не удалась» по кнопке
// «Заказать снова» (FR-053а): строка заказа выделена, открыта его карточка, фокус — на её
// «Заказать снова» (одно нажатие — и заказ повторён; T146), и так на каждый новый номер, в том
// числе когда страница уже открыта (T119). Сам заказ по адресу не повторяется: человек видит
// условия в карточке и решает (ux.md, раздел 1).
// В заголовке — ссылка «Открыть журнал» (на кнопке — действие и объект, README «Тексты»; T157);
// заказов нет — та же ссылка в пустом состоянии, а в заголовке её нет.
// Строка открывает карточку заказа в боковой панели без затемнения: условия словами, записей,
// состояние, когда заказан и до какого времени хранится, номер операции заказа, у неудавшейся —
// причина и код; те же действия, что в меню строки (ux.md, раздел 4; T139). Список остаётся
// живым: клик по другой строке меняет карточку.
import {
  auditExportStateLabels,
  exportFailureReason,
  formatCount,
  formatNumber,
} from '@kp/contracts';
import type { AuditExport, AuditExportState } from '@kp/contracts';
import {
  Badge,
  Button,
  DataTable,
  Drawer,
  DropdownItem,
  DropdownSeparator,
  EmptyState,
  OperationCode,
  PageHeader,
  buttonClass,
  useToast,
} from '@kp/ui';
import type { BadgeTone, DataTableColumn } from '@kp/ui';
import { Download, FileSpreadsheet, RotateCcw, Trash2 } from '@kp/ui/icons';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { api, apiKey, isApiError } from '../../api/client.ts';
import { cancelDeferred, commitDeferred, defer } from '../../app/deferred.ts';
import type { CommitMode } from '../../app/deferred.ts';
import { errorView, useErrorToast } from '../../app/errors.ts';
import { dateIn, formatDateTime } from '../../app/format.ts';
import { useTimeZone } from '../../shell/session.ts';
import { LoadErrorState } from '../load-error.tsx';
import { OperationText } from '../ops/OperationText.tsx';
import { saveFile } from './save-file.ts';
import './exports.css';

/** Сколько живёт тост «Файл удалён · Отменить» (contracts/ui.md). */
export const DELETE_UNDO_MS = 10_000;

const stateTones = {
  preparing: 'accent',
  ready: 'ok',
  failed: 'bad',
  deleted: 'neutral',
} as const satisfies Record<AuditExportState, BadgeTone>;

const listKey = ['api', 'audit.exports.list'] as const;

const DAYS = ['день', 'дня', 'дней'] as const;

/**
 * Пояснение под заголовком. Срок хранения файла — из ответа сервера (настройка установки
 * retention.exportFileDays, FR-055), а не из кода экрана (принцип VIII); пока списка нет —
 * без срока.
 */
export function exportsNote(retentionDays: number | undefined): string {
  const keep =
    retentionDays === undefined
      ? 'Скачать файл можете только вы'
      : `Файл хранится ${formatCount(retentionDays, DAYS)}, скачать его можете только вы`;
  return `Большие выгрузки журнала собираются в фоне. ${keep}`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ExportsSearch {
  /** Заказ, на который ведёт уведомление о неудаче. */
  focus?: string;
}

/** Адрес «Моих выгрузок»: только номер заказа, остальное отбрасывается. */
export function parseExportsSearch(search: Record<string, unknown>): ExportsSearch {
  return typeof search.focus === 'string' && UUID.test(search.focus) ? { focus: search.focus } : {};
}

/**
 * Состояние словом — бейдж не только цветом. У неудавшейся — причина словами (у OPS-003 —
 * «Выгрузку отменило сопровождение») и «Код … · операция …» заказа с «Скопировать номер»:
 * по ним сопровождение находит записи технического журнала (FR-028, принцип V).
 */
function StateCell({ item }: { item: AuditExport }) {
  const badge = (
    <Badge tone={stateTones[item.state]} dot>
      {auditExportStateLabels[item.state]}
    </Badge>
  );
  if (item.state !== 'failed' || !item.errorCode) return badge;
  return (
    <span className="kp-export-failed">
      {badge}
      <span className="kp-export-reason">{exportFailureReason(item.errorCode)}</span>
      <OperationCode code={item.errorCode} operation={item.operation} />
    </span>
  );
}

/** Полоса состояния карточки: что с файлом сейчас (ux.md, раздел 4). */
function cardStatusText(item: AuditExport, timeZone: string): string {
  switch (item.state) {
    case 'ready':
      return item.expiresAt
        ? `Файл готов, хранится до ${formatDateTime(item.expiresAt, timeZone)}`
        : 'Файл готов';
    case 'failed':
      return 'Выгрузка не удалась';
    case 'preparing':
      return 'Файл готовится. Пришлём уведомление, когда он будет готов';
    case 'deleted':
      return 'Файл удалён по сроку хранения';
  }
}

interface ExportActions {
  readonly busy: boolean;
  readonly onDownload: () => void;
  readonly onOrderAgain: () => void;
  readonly onDelete: () => void;
}

/** Фокус на «Заказать снова» карточки: заказ открыт из уведомления о неудаче (?focus=). */
interface OrderAgainFocus {
  readonly pending: boolean;
  readonly onFocused: () => void;
  /** Куда вернуть фокус после карточки: кнопки уведомления, которая её открыла, уже нет. */
  readonly returnFocusRef: RefObject<HTMLElement | null>;
}

/**
 * Карточка заказа (ux.md, раздел 4; T139): условия словами, записей, когда заказан, до какого
 * времени хранится файл, номер операции заказа; у неудавшейся — причина словами и «Код … ·
 * операция …» с «Скопировать номер». Действия — в полосе состояния, как в строке: «Скачать файл»
 * у готовой, «Заказать снова» у неудавшейся, «Удалить выгрузку» у обеих.
 */
function ExportCard({
  item,
  timeZone,
  actions,
  focusAgain,
  onClose,
}: {
  item: AuditExport;
  timeZone: string;
  actions: ExportActions;
  focusAgain: OrderAgainFocus;
  onClose: () => void;
}) {
  const failed = item.state === 'failed';
  const removable = item.state === 'ready' || failed;
  // Фокус ставится, как только кнопка в панели: Radix не переводит его на саму панель, если он
  // уже внутри; у открытой панели, сменившей заказ, — сразу.
  const focusOrderAgain = (node: HTMLButtonElement | null) => {
    if (!node || !focusAgain.pending) return;
    focusAgain.onFocused();
    node.focus();
  };
  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Выгрузка от ${formatDateTime(item.createdAt, timeZone)}`}
      returnFocusRef={focusAgain.returnFocusRef}
      headerActions={
        <Badge tone={stateTones[item.state]} dot>
          {auditExportStateLabels[item.state]}
        </Badge>
      }
      statusTone={failed ? 'bad' : 'info'}
      status={
        <>
          <span>{cardStatusText(item, timeZone)}</span>
          {item.state === 'ready' ? (
            <Button size="sm" icon={Download} loading={actions.busy} onClick={actions.onDownload}>
              Скачать файл
            </Button>
          ) : null}
          {failed ? (
            <Button
              ref={focusOrderAgain}
              size="sm"
              icon={RotateCcw}
              loading={actions.busy}
              onClick={actions.onOrderAgain}
            >
              Заказать снова
            </Button>
          ) : null}
          {removable ? (
            <Button size="sm" variant="ghost" icon={Trash2} onClick={actions.onDelete}>
              Удалить выгрузку
            </Button>
          ) : null}
        </>
      }
    >
      <dl className="t-kv kp-props">
        <dt>Условия</dt>
        <dd>{item.filtersSummary}</dd>
        <dt>Записей</dt>
        <dd className="t-num">
          {item.recordsCount === undefined ? '—' : formatNumber(item.recordsCount)}
        </dd>
        <dt>Заказано</dt>
        <dd className="t-num">{formatDateTime(item.createdAt, timeZone)}</dd>
        {item.state === 'ready' && item.expiresAt ? (
          <>
            <dt>Хранится до</dt>
            <dd className="t-num">{formatDateTime(item.expiresAt, timeZone)}</dd>
          </>
        ) : null}
        <dt>Операция</dt>
        <dd>
          <OperationText operation={item.operation} />
        </dd>
      </dl>
      {failed && item.errorCode ? (
        <section className="kp-section" aria-labelledby="kp-export-failure">
          <h3 className="t-sec-h" id="kp-export-failure">
            Почему не удалась
          </h3>
          <p>{exportFailureReason(item.errorCode)}</p>
          <OperationCode code={item.errorCode} operation={item.operation} />
        </section>
      ) : null}
    </Drawer>
  );
}

export function ExportsPage({ focus }: ExportsSearch = {}) {
  const timeZone = useTimeZone();
  // Заказ из уведомления — один раз на каждый новый номер в адресе, когда он в списке «Не
  // удалась»: переход из уведомления перечитывает список, и заказ, который был «Готовится»,
  // становится «Не удалась» (T119). Тогда открывается его карточка, фокус — на её «Заказать
  // снова» (T146).
  const [focusPending, setFocusPending] = useState(focus);
  const [syncedFocus, setSyncedFocus] = useState(focus);
  if (focus !== syncedFocus) {
    setSyncedFocus(focus);
    setFocusPending(focus);
  }
  /** Карточка какого заказа ставит фокус на «Заказать снова», как только кнопка появится. */
  const [focusAgainId, setFocusAgainId] = useState<string | null>(null);
  const toast = useToast();
  const showError = useErrorToast();
  const queryClient = useQueryClient();
  // «…» строк по номеру заказа и заголовок страницы — куда перевести фокус, когда строка скрылась
  // вместе со своей «…» (T124, T146).
  const menuButtons = useRef(new Map<string, HTMLButtonElement>());
  const headingRef = useRef<HTMLSpanElement>(null);
  // Карточку открыло уведомление — после неё фокус на «…» строки заказа; открыла строка — на
  // строку (по умолчанию панели).
  const cardReturnRef = useRef<HTMLElement | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: apiKey('audit.exports.list'),
    queryFn: ({ pageParam, signal }) =>
      api('audit.exports.list', { query: { cursor: pageParam }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const rows = useMemo(
    () =>
      (list.data?.pages.flatMap((page) => page.items) ?? []).filter((item) => !hidden.has(item.id)),
    [list.data, hidden],
  );
  // Заказ из уведомления уже «Не удался» — его карточка открыта, фокус ждёт «Заказать снова».
  const fromNotification =
    focusPending === undefined
      ? undefined
      : rows.find((item) => item.id === focusPending && item.state === 'failed');
  if (fromNotification) {
    setFocusPending(undefined);
    setFocusAgainId(fromNotification.id);
    setOpenId(fromNotification.id);
  }
  // Карточка — последняя версия заказа из списка; заказ скрыли или удалили — карточки нет.
  const opened = rows.find((item) => item.id === openId) ?? null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: listKey });
  const setVisible = (id: string, on: boolean) =>
    setHidden((current) => {
      const next = new Set(current);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });

  const download = async (item: AuditExport) => {
    setBusy(item.id);
    try {
      const file = await api('audit.exports.file', { params: { id: item.id } });
      saveFile(file, `журнал-${dateIn(timeZone, new Date(item.snapshotAt))}.xlsx`);
    } catch (error) {
      showError(error);
      // Файл удалён по сроку или заказ изменился — список перечитывается.
      if (isApiError(error)) void refresh();
    } finally {
      setBusy(null);
    }
  };

  /**
   * Запрос на удаление — когда отменить уже нельзя. Не прошёл — строка возвращается, тост-ошибка
   * с «Повторить»: та же операция — строка снова скрывается, и запрос уходит, как после «Удалить».
   */
  const commitDelete = async (item: AuditExport, { final, keepalive }: CommitMode) => {
    try {
      await api('audit.exports.delete', { params: { id: item.id }, background: final, keepalive });
      void refresh();
    } catch (error) {
      setVisible(item.id, true);
      showError(error, () => remove(item));
    }
  };

  /**
   * Строка скрывается вместе со своей «…» — фокус не падает на страницу: на «…» следующей строки
   * с действиями, у последней — предыдущей, таких нет — на заголовок «Мои выгрузки» (T124, T146).
   */
  const focusAfterHide = (item: AuditExport) => {
    const index = rows.findIndex((row) => row.id === item.id);
    const around = [...rows.slice(index + 1), ...rows.slice(0, index).reverse()];
    const next = around
      .map((row) => menuButtons.current.get(row.id))
      .find((button) => button !== undefined);
    (next ?? headingRef.current)?.focus();
  };

  const remove = (item: AuditExport) => {
    focusAfterHide(item);
    setVisible(item.id, false);
    // Карточка скрытого заказа закрывается вместе со строкой: фокус уже перешёл на соседнюю.
    setOpenId((current) => (current === item.id ? null : current));
    const shown = toast.ok('Файл удалён', {
      duration: DELETE_UNDO_MS,
      action: {
        label: 'Отменить',
        onClick: () => {
          if (cancelDeferred(item.id)) setVisible(item.id, true);
        },
      },
      onDismiss: () => void commitDeferred(item.id),
    });
    // Без связи тост об успехе не показывается — отменить нечем: запрос сразу.
    if (shown === 0) {
      void commitDelete(item, { final: false, keepalive: false });
      return;
    }
    defer(item.id, {
      commit: (mode) => commitDelete(item, mode),
      close: () => toast.dismiss(shown),
    });
  };

  const orderAgain = async (item: AuditExport) => {
    setBusy(item.id);
    try {
      await api('audit.exports.create', { body: item.filters });
      toast.ok('Выгрузка заказана снова. Пришлём уведомление');
      void refresh();
    } catch (error) {
      showError(error, () => void orderAgain(item));
    } finally {
      setBusy(null);
    }
  };

  const columns: DataTableColumn<AuditExport>[] = [
    {
      id: 'createdAt',
      header: 'Заказано',
      value: (item) => formatDateTime(item.createdAt, timeZone),
      numeric: true,
    },
    { id: 'filters', header: 'Условия', value: (item) => item.filtersSummary, width: '34%' },
    {
      id: 'records',
      header: 'Записей',
      value: (item) => (item.recordsCount === undefined ? '—' : formatNumber(item.recordsCount)),
      numeric: true,
    },
    { id: 'state', header: 'Состояние', cell: (item) => <StateCell item={item} /> },
    {
      id: 'expiresAt',
      header: 'Хранится до',
      value: (item) =>
        item.expiresAt && item.state === 'ready' ? formatDateTime(item.expiresAt, timeZone) : '—',
      numeric: true,
      hideable: true,
    },
  ];

  /**
   * Меню «…» строки собирается на каждый показ и читает свежее состояние: «Скачать файл» у готовой,
   * «Заказать снова» у неудавшейся, за разделителем «Удалить выгрузку» у обеих. У готовящейся
   * и удалённой по сроку действий нет.
   */
  const rowMenu = (item: AuditExport) => {
    if (item.state !== 'ready' && item.state !== 'failed') return null;
    const waiting = busy === item.id;
    return (
      <>
        {item.state === 'ready' ? (
          <DropdownItem icon={Download} disabled={waiting} onSelect={() => void download(item)}>
            Скачать файл
          </DropdownItem>
        ) : (
          <DropdownItem icon={RotateCcw} disabled={waiting} onSelect={() => void orderAgain(item)}>
            Заказать снова
          </DropdownItem>
        )}
        <DropdownSeparator />
        <DropdownItem icon={Trash2} danger onSelect={() => remove(item)}>
          Удалить выгрузку
        </DropdownItem>
      </>
    );
  };

  const loadError = list.error ? errorView(list.error) : null;

  return (
    <main id="main" tabIndex={-1} className="kp-page">
      <PageHeader
        title={
          <span ref={headingRef} tabIndex={-1}>
            Мои выгрузки
          </span>
        }
        note={exportsNote(list.data?.pages[0]?.retentionDays)}
        secondaryActions={
          // Заказов нет — «Открыть журнал» в пустом состоянии, второй такой ссылки нет.
          list.isSuccess && rows.length === 0 ? undefined : (
            <Link to="/audit" className={buttonClass({ variant: 'ghost', size: 'sm' })}>
              Открыть журнал
            </Link>
          )
        }
      />
      <DataTable
        label="Мои выгрузки"
        columns={columns}
        rows={rows}
        getRowId={(item) => item.id}
        onRowClick={(item) => {
          cardReturnRef.current = null;
          setOpenId(item.id);
        }}
        rowMenu={rowMenu}
        rowMenuLabel={(item) => `Действия: выгрузка от ${formatDateTime(item.createdAt, timeZone)}`}
        rowMenuRef={(id, element) => {
          if (element) menuButtons.current.set(id, element);
          else menuButtons.current.delete(id);
        }}
        selectedRowId={openId ?? focus ?? null}
        columnsMenu={rows.length > 0}
        loading={list.isPending}
        empty={
          <EmptyState
            icon={FileSpreadsheet}
            title="Вы ещё не заказывали выгрузку"
            text="Большие выгрузки журнала собираются здесь"
            action={
              <Link to="/audit" className={buttonClass({ variant: 'secondary' })}>
                Открыть журнал
              </Link>
            }
          />
        }
        error={
          loadError ? (
            <LoadErrorState
              view={loadError}
              onRetry={isApiError(list.error, 'ACC-003') ? undefined : () => void list.refetch()}
              retrying={list.isFetching}
            />
          ) : undefined
        }
        hasMore={list.hasNextPage}
        loadingMore={list.isFetchingNextPage}
        onLoadMore={() => void list.fetchNextPage()}
      />
      {opened ? (
        <ExportCard
          item={opened}
          timeZone={timeZone}
          actions={{
            busy: busy === opened.id,
            onDownload: () => void download(opened),
            onOrderAgain: () => void orderAgain(opened),
            onDelete: () => remove(opened),
          }}
          focusAgain={{
            pending: focusAgainId === opened.id,
            onFocused: () => {
              cardReturnRef.current = menuButtons.current.get(opened.id) ?? null;
              setFocusAgainId(null);
            },
            returnFocusRef: cardReturnRef,
          }}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </main>
  );
}
